import React, { useState, useEffect } from 'react';
import { getProjectSettings, updateProjectSettings } from '../services/api';
import {
  getProjectSettingsFromSupabase,
  updateProjectSettingsInSupabase,
  updateBYOKSettingsInSupabase
} from '../services/supabase';
import { SubscriptionTier, TIER_DISPLAY_NAMES, TIER_BADGE_COLORS } from '../types';
import './ProjectSettings.css';

interface ProjectSettingsProps {
  projectId: string;
  useSupabase?: boolean; // If true, use Supabase Edge Functions directly
  isAdmin?: boolean; // Whether the current user is an admin
}

const ProjectSettings: React.FC<ProjectSettingsProps> = ({ projectId, useSupabase = true, isAdmin = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [companyName, setCompanyName] = useState('');
  const [description, setDescription] = useState('');
  const [verbosity, setVerbosity] = useState('nominal');
  const [alwaysIncludePolicyContext, setAlwaysIncludePolicyContext] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>('free');

  // BYOK state (admin only)
  const [byokEnabled, setByokEnabled] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [savingByok, setSavingByok] = useState(false);
  const [byokError, setByokError] = useState<string | null>(null);
  const [byokSuccess, setByokSuccess] = useState<string | null>(null);

  const loadSettings = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (useSupabase) {
        // Use Supabase Edge Functions directly
        const data = await getProjectSettingsFromSupabase(projectId);
        setCompanyName(data.company_name);
        setDescription(data.description || '');
        setVerbosity(data.response_verbosity);
        setAlwaysIncludePolicyContext(data.always_include_policy_context || false);
        setSubscriptionTier(data.subscription_tier || 'free');

        // BYOK settings are included in the response for admins
        if (isAdmin && data.byok_enabled !== undefined) {
          setByokEnabled(data.byok_enabled);
          setHasApiKey(data.has_api_key || false);
        }
      } else {
        // Fallback to backend API
        const data = await getProjectSettings(projectId);
        setCompanyName(data.company_name);
        setDescription(data.description || '');
        setVerbosity(data.response_verbosity);
        setAlwaysIncludePolicyContext(data.always_include_policy_context || false);
      }
    } catch (err: any) {
      setError(err.message || err.response?.data?.detail || 'Failed to load project settings');
    } finally {
      setLoading(false);
    }
  }, [projectId, useSupabase, isAdmin]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (useSupabase) {
        // Use Supabase Edge Functions directly
        await updateProjectSettingsInSupabase(projectId, {
          company_name: companyName,
          description: description || null,
          response_verbosity: verbosity,
          always_include_policy_context: alwaysIncludePolicyContext
        });
      } else {
        // Fallback to backend API
        await updateProjectSettings(projectId, {
          company_name: companyName,
          description: description || null,
          response_verbosity: verbosity,
          always_include_policy_context: alwaysIncludePolicyContext
        });
      }

      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveByok = async () => {
    try {
      setSavingByok(true);
      setByokError(null);
      setByokSuccess(null);

      // Validate: if enabling BYOK, API key is required
      if (byokEnabled && !hasApiKey && !newApiKey.trim()) {
        setByokError('API key is required when enabling this feature');
        return;
      }

      const result = await updateBYOKSettingsInSupabase(projectId, {
        byok_enabled: byokEnabled,
        gemini_api_key: newApiKey.trim() || undefined
      });

      setHasApiKey(result.has_api_key);
      setNewApiKey(''); // Clear the input after save
      setByokSuccess('Custom API key settings saved successfully!');
      setTimeout(() => setByokSuccess(null), 3000);
    } catch (err: any) {
      setByokError(err.message || 'Failed to save API key settings');
    } finally {
      setSavingByok(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="loading-spinner"></div>
        <span>Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="project-settings">
      <div className="settings-card">
        <div className="settings-header">
          <h2>Project Configuration</h2>
          <p>Manage your project settings and preferences</p>
        </div>

        {error && <div className="settings-error">{error}</div>}
        {success && <div className="settings-success">{success}</div>}

        <div className="settings-form">
          <div className="form-group">
            <label htmlFor="projectId">Project ID</label>
            <input
              type="text"
              id="projectId"
              value={projectId}
              disabled
              className="input-disabled"
            />
            <span className="field-hint">Project ID cannot be changed</span>
          </div>

          <div className="form-group">
            <label>Subscription Tier</label>
            <div className="tier-display">
              <span
                className="tier-badge-settings"
                style={{ backgroundColor: TIER_BADGE_COLORS[subscriptionTier] }}
              >
                {TIER_DISPLAY_NAMES[subscriptionTier]}
              </span>
              <a href="#subscription" className="tier-manage-link">Manage subscription</a>
            </div>
            <span className="field-hint">Your current subscription plan</span>
          </div>

          <div className="form-group">
            <label htmlFor="companyName">Company Name</label>
            <input
              type="text"
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Enter company name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter project description"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="verbosity">Response Verbosity</label>
            <select
              id="verbosity"
              value={verbosity}
              onChange={(e) => setVerbosity(e.target.value)}
            >
              <option value="minimal">Minimal - Brief responses</option>
              <option value="nominal">Nominal - Standard responses</option>
              <option value="verbose">Verbose - Detailed responses</option>
            </select>
            <span className="field-hint">
              Controls how detailed the AI-generated responses will be
            </span>
          </div>

          <div className="form-group">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={alwaysIncludePolicyContext}
                onChange={(e) => setAlwaysIncludePolicyContext(e.target.checked)}
              />
              <span className="toggle-text">Always include policy documents in responses</span>
            </label>
            <span className="field-hint">
              When enabled, policy document context will be sent to the AI even when a matching
              answer is found in the Q&A database. This ensures responses always reference the
              latest policy documents but may increase response time and API costs.
            </span>
          </div>

          <div className="form-actions">
            <button
              className="btn-secondary"
              onClick={loadSettings}
              disabled={saving}
            >
              Reset
            </button>
            <button
              className="btn-primary"
              onClick={handleSave}
              disabled={saving || !companyName.trim()}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Custom API Key Section - Admin Only */}
      {isAdmin && (
        <div className="settings-card byok-section">
          <div className="settings-header">
            <h2>🔑 Custom API Key</h2>
            <p>Use your own AI API key for this project</p>
          </div>

          {byokError && <div className="settings-error">{byokError}</div>}
          {byokSuccess && <div className="settings-success">{byokSuccess}</div>}

          <div className="settings-form">
            <div className="form-group">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={byokEnabled}
                  onChange={(e) => setByokEnabled(e.target.checked)}
                />
                <span className="toggle-text">Use my own API key</span>
              </label>
              <span className="field-hint">
                When enabled, your project will use your own API key instead of the shared key.
                This gives you full control over API usage and billing.
              </span>
            </div>

            {byokEnabled && (
              <>
                <div className="form-group">
                  <label htmlFor="apiKey">API Key</label>
                  <input
                    type="password"
                    id="apiKey"
                    value={newApiKey}
                    onChange={(e) => setNewApiKey(e.target.value)}
                    placeholder={hasApiKey ? '••••••••••••••••' : 'Enter your API key'}
                  />
                  <span className="field-hint">
                    {hasApiKey
                      ? 'An API key is already configured. Enter a new key to replace it.'
                      : 'Get your API key from Google AI Studio (https://aistudio.google.com/apikey)'}
                  </span>
                </div>

                {hasApiKey && (
                  <div className="byok-status">
                    <span className="status-indicator status-active"></span>
                    <span>API key is configured and active</span>
                  </div>
                )}
              </>
            )}

            <div className="form-actions">
              <button
                className="btn-primary"
                onClick={handleSaveByok}
                disabled={savingByok || (byokEnabled && !hasApiKey && !newApiKey.trim())}
              >
                {savingByok ? 'Saving...' : 'Save API Key Settings'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSettings;

